/**
 * Playing her.
 *
 * Everything here is built around one rule: a sound that arrives late is worse
 * than no sound. Her cues are rare and small, so the temptation is to make the
 * engine robust — queue a cue that arrives before the file has downloaded,
 * retry a decode, resume a context that is not ready. All of that produces a
 * chime somewhere in the middle of the *next* thing you do, attached to
 * nothing, which reads as a bug rather than as a narrator. So every failure
 * path here is the same one: do nothing, quietly.
 *
 * Files are fetched on first use and kept for the session, so an app that
 * never makes a sound never downloads one. The code is small and always
 * loaded; the two hundred kilobytes that matter are the assets, and those stay
 * on the server until something actually asks for them.
 */

import { currentPresence } from '../presence/store.js'
import { audioContext } from './context.js'
import { CUES, type CueName, LOOP, MASTER_GAIN, TONE_SHAPING } from './manifest.js'
import { soundEnabled } from './settings.js'

const buffers = new Map<CueName, AudioBuffer>()
const loading = new Map<CueName, Promise<AudioBuffer | null>>()

/** The looping bed, while a performance is up. At most one at a time. */
let bed: { source: AudioBufferSourceNode; gain: GainNode } | null = null

function load(cue: CueName): Promise<AudioBuffer | null> {
  const cached = buffers.get(cue)
  if (cached != null) return Promise.resolve(cached)
  const inFlight = loading.get(cue)
  if (inFlight != null) return inFlight

  const promise = (async () => {
    const ctx = audioContext()
    if (ctx == null) return null
    try {
      const response = await fetch(`/sounds/${CUES[cue].file}.m4a`)
      if (!response.ok) return null
      const buffer = await ctx.decodeAudioData(await response.arrayBuffer())
      buffers.set(cue, buffer)
      return buffer
    } catch {
      // A missing or undecodable file is not worth reporting to anybody: the
      // app's job does not depend on it, and the alternative is an error toast
      // about a sound, which is worse than the silence it is complaining about.
      return null
    } finally {
      loading.delete(cue)
    }
  })()
  loading.set(cue, promise)
  return promise
}

/**
 * Play one cue, shaped by whatever mood she is in.
 *
 * The shaping is a playback rate rather than a filter, so a gloomy cue is the
 * same voice slowed down the way a tape slows down — which is the artefact
 * wanted here. A separate sample per mood would be seven times the download
 * for a difference nobody could name.
 */
export function playCue(cue: CueName): void {
  if (!soundEnabled()) return
  const ctx = audioContext()
  if (ctx == null) return

  void load(cue).then((buffer) => {
    if (buffer == null) return
    // Re-checked after the await: a download that lands during a performance,
    // or after the setting was switched off, has missed its moment.
    if (!soundEnabled()) return
    const live = audioContext()
    if (live == null) return

    const def = CUES[cue]
    const shape = TONE_SHAPING[currentPresence().tone] ?? { rate: 1, gain: 1 }
    try {
      const source = live.createBufferSource()
      const gain = live.createGain()
      source.buffer = buffer
      source.playbackRate.value = shape.rate
      gain.gain.value = def.gain * shape.gain * MASTER_GAIN
      source.connect(gain).connect(live.destination)
      source.start()
    } catch {
      // An interrupted or closed context; see the module header.
    }
  })
}

/**
 * Start the bed under a performance, fading in rather than cutting in — a
 * loop that begins at full volume announces itself as a loop.
 */
export function startBed(): void {
  if (!soundEnabled() || bed != null) return
  void load('ceremony').then((buffer) => {
    const live = audioContext()
    if (buffer == null || live == null || bed != null || !soundEnabled()) return
    try {
      const source = live.createBufferSource()
      const gain = live.createGain()
      source.buffer = buffer
      source.loop = true
      // Loops the middle period rather than the whole file, to stay clear of
      // the encoder's priming samples. See `LOOP`.
      source.loopStart = LOOP.start
      source.loopEnd = LOOP.end
      const target = CUES.ceremony.gain * MASTER_GAIN
      gain.gain.setValueAtTime(0.0001, live.currentTime)
      gain.gain.exponentialRampToValueAtTime(target, live.currentTime + 1.4)
      source.connect(gain).connect(live.destination)
      source.start()
      bed = { source, gain }
    } catch {
      bed = null
    }
  })
}

/** Stop the bed, fading out. Safe to call when nothing is playing. */
export function stopBed(): void {
  const playing = bed
  bed = null
  if (playing == null) return
  const live = audioContext()
  try {
    if (live != null) {
      playing.gain.gain.cancelScheduledValues(live.currentTime)
      playing.gain.gain.setValueAtTime(Math.max(0.0001, playing.gain.gain.value), live.currentTime)
      playing.gain.gain.exponentialRampToValueAtTime(0.0001, live.currentTime + 0.5)
      playing.source.stop(live.currentTime + 0.55)
    } else {
      playing.source.stop()
    }
  } catch {
    // Already stopped, or the context went away underneath it.
  }
}

/**
 * A phone that goes to sleep with a ceremony open must not keep humming in a
 * pocket. Registered once, from the app root.
 */
export function stopBedWhenHidden(): () => void {
  const check = () => {
    if (document.visibilityState === 'hidden') stopBed()
  }
  document.addEventListener('visibilitychange', check)
  return () => document.removeEventListener('visibilitychange', check)
}

/** Test-only: forget every decoded buffer. */
export function __resetSoundForTests(): void {
  buffers.clear()
  loading.clear()
  bed = null
}
